import { Card, Collapse, Divider, Table, Tag, Typography } from "antd";

import "../styles.css";

const { Title, Paragraph, Text } = Typography;
const { Panel } = Collapse;

/**
 * Plain-language explainer for the coins / Watch & Earn / unlock system —
 * written for content and operations folks, not developers. Pure static
 * page: no API calls, safe for every CMS role.
 */

const EXAMPLE_ROWS = [
  {
    key: "1",
    step: "1. Free episodes",
    what: "Viewer watches episodes 1–2 free (Free Episodes = 2 on the series).",
    coins: "—",
  },
  {
    key: "2",
    step: "2. Watch & Earn task",
    what:
      "The Tasks tab shows “Watch 2 episodes of Arjun Bihari”. As they watch, the counter fills (1/2, 2/2).",
    coins: "—",
  },
  {
    key: "3",
    step: "3. Reward — automatic",
    what:
      "The moment the 2nd episode finishes, the reward is claimed automatically. No button needed.",
    coins: "+200",
  },
  {
    key: "4",
    step: "4. Unlock — automatic",
    what:
      "The reward instantly buys the next bundle of episodes: 5 episodes for 150 coins → episodes 3–7 open up.",
    coins: "−150",
  },
  {
    key: "5",
    step: "5. The loop repeats",
    what:
      "They watch the 5 unlocked episodes → new reward → next 5 unlock (8–12) … all the way to the end of the series.",
    coins: "+200 / −150 each round",
  },
];

const HowItWorks = () => (
  <div className="manage-tasks-wrapper">
    <Typography style={{ maxWidth: 860 }}>
      <Title level={3}>How Coins, Watch &amp; Earn and Unlocks work</Title>
      <Paragraph type="secondary">
        A viewer-friendly reward loop: people watch free episodes, earn coins
        for finishing them, and those coins automatically open up the next
        episodes — so an engaged viewer can keep watching without paying,
        while casual viewers still hit the paywall.
      </Paragraph>

      <Card size="small" style={{ marginBottom: 16 }}>
        <Paragraph style={{ marginBottom: 0 }}>
          <Text strong>The loop in one line:</Text> Watch{" "}
          <Tag color="blue">X episodes</Tag> → earn{" "}
          <Tag color="gold">R coins</Tag> automatically → coins auto-buy the{" "}
          <Tag color="purple">next Y episodes</Tag> → keep watching → repeat.
        </Paragraph>
      </Card>

      <Title level={4}>A worked example (real numbers)</Title>
      <Paragraph type="secondary">
        Series set up with Free Episodes = 2, Watch &amp; Earn = “2 episodes →
        200 coins”, bundle price 150 coins for 5 episodes:
      </Paragraph>
      <Table
        columns={[
          { title: "Step", dataIndex: "step", width: 190 },
          { title: "What happens", dataIndex: "what" },
          { title: "Coins", dataIndex: "coins", width: 170 },
        ]}
        dataSource={EXAMPLE_ROWS}
        pagination={false}
        size="small"
        style={{ marginBottom: 24 }}
      />

      <Divider />

      <Title level={4}>What each page here controls</Title>
      <Collapse defaultActiveKey={[]} style={{ marginBottom: 24 }}>
        <Panel header="Watch Earn Config — the earning side" key="we">
          <Paragraph>
            The <Text strong>master switch</Text> turns the whole Watch &amp;
            Earn feature on or off. Below it, each series gets two numbers:
          </Paragraph>
          <Paragraph>
            • <Text strong>Episodes (X)</Text> — how many episodes must be
            fully watched (at least 80% of an episode counts as watched).
            <br />• <Text strong>Coins (R)</Text> — the reward for finishing
            them.
          </Paragraph>
          <Paragraph>
            A series only shows a task card when BOTH numbers are set and the
            master switch is on. Progress <Text strong>carries over days</Text>{" "}
            (watch 1 today, 1 tomorrow → reward), and after each reward the
            counter starts fresh so the task repeats forever. Each episode
            counts <Text strong>once per reward round</Text> — after a claim,
            ANY episode (rewatches included) counts toward the next round,
            but replaying one episode within the same round never adds
            progress twice.
          </Paragraph>
          <Paragraph type="secondary">
            Rewards are claimed automatically the moment the last episode
            finishes — viewers never need to press a Claim button. (The button
            still exists in the app purely as a backup if the automatic claim
            hits a temporary error.)
          </Paragraph>
          <Paragraph>
            <Text strong>Max claims per user</Text> (optional, per series) —
            limits how many times ONE viewer can complete the task. Blank =
            unlimited, so the watch → earn → unlock loop can run to the end
            of the series. With a cap of e.g. 3, the card disappears for that
            viewer after their 3rd reward and further claims are refused.
          </Paragraph>
        </Panel>

        <Panel header="Unlock Config + Series/Episode Coins — the spending side" key="unlock">
          <Paragraph>
            Coins are spent on unlocking episodes, in two products:
          </Paragraph>
          <Paragraph>
            • <Text strong>Bundle</Text> — “next N episodes” for a fixed coin
            price (this is what Watch &amp; Earn rewards buy automatically).
            <br />• <Text strong>Full series</Text> — everything at once, for
            a bigger price (offered on the paywall).
          </Paragraph>
          <Paragraph>
            <Text strong>Unlock Config</Text> sets the global defaults
            (bundle price, bundle size, series price, validity in days).{" "}
            <Text strong>Series Coins / Episode Coins</Text> can override
            these per series — leave a field empty to use the default, or set
            it to 0 to switch that product off for that series. Unlocks
            expire after the validity period (default 30 days), after which
            the episodes lock again.
          </Paragraph>
        </Panel>

        <Panel header="How the automatic unlock is calculated" key="calc">
          <Paragraph>
            When a reward lands, the system spends it on{" "}
            <Text strong>whole bundles only</Text>:
          </Paragraph>
          <Paragraph>
            <Text code>bundles = reward coins ÷ bundle price</Text> (rounded
            down) → <Text code>episodes = bundles × bundle size</Text>,
            starting from the first locked episode the viewer doesn’t already
            own. Example: 200 coin reward, bundle 150/5 → one bundle → 5
            episodes, and 50 coins stay in the wallet.
          </Paragraph>
          <Paragraph type="secondary">
            If the reward is smaller than one bundle, nothing is auto-bought —
            the viewer keeps the coins and can save up. Viewers with an active
            subscription just get the coins (nothing is locked for them).
          </Paragraph>
        </Panel>

        <Panel header="Watch Ladder — the daily escalating reward" key="ladder">
          <Paragraph>
            Configured on the <Text strong>Watch Ladder</Text> page —
            per series: a series with the ladder switch ON shows the
            escalating daily ladder{" "}
            <Text strong>instead of</Text> its Watch &amp; Earn card
            (either/or — the ladder wins). The numbers (X, Y, levels) are
            global; each completed level pays more than the last. Levels
            credit <Text strong>automatically the moment they complete</Text>{" "}
            — no claim button — and the ladder{" "}
            <Text strong>resets at IST midnight</Text>.
          </Paragraph>
          <Paragraph type="secondary">
            Example day with X = 3 episodes per level, Y = 50 base coins, 3
            levels:
          </Paragraph>
          <Table
            columns={[
              { title: "Viewer watches", dataIndex: "watch", width: 170 },
              { title: "What happens", dataIndex: "what" },
              { title: "Coins", dataIndex: "coins", width: 110 },
            ]}
            dataSource={[
              {
                key: "1",
                watch: "Episodes 1–3 of that series",
                what: "Level 1 completes → 1×Y credits instantly. Card advances to Level 2 showing the bigger reward.",
                coins: "+50",
              },
              {
                key: "2",
                watch: "3 more episodes",
                what: "Level 2 completes → 2×Y credits instantly.",
                coins: "+100",
              },
              {
                key: "3",
                watch: "3 more episodes",
                what: "Level 3 completes → 3×Y credits. Card shows “Daily ladder complete — 300 coins earned!” until midnight.",
                coins: "+150",
              },
              {
                key: "4",
                watch: "After IST midnight",
                what: "Ladder resets to Level 1 — the same episodes can count again on the new day.",
                coins: "—",
              },
            ]}
            pagination={false}
            size="small"
            style={{ marginBottom: 12 }}
          />
          <Paragraph>
            Rules to remember: each episode counts <Text strong>once per
            day</Text> (rewatching the same episode that day doesn't move the
            ladder); a ladder series never double-pays — its Watch &amp; Earn
            card is hidden while the ladder is on; and all ladder coins count
            toward the viewer's daily earning cap. Set the numbers so a full
            ladder (Y + 2Y + …) stays within that cap.
          </Paragraph>
        </Panel>

        <Panel header="Other ways viewers earn" key="other">
          <Paragraph>
            • <Text strong>Daily app open</Text> — a small automatic reward,
            credited the moment the app is first opened each day.
            <br />• <Text strong>PubScale offers</Text> — third-party tasks
            (install an app, take a survey). Curated on the PubScale Ads
            page; coins arrive after the advertiser confirms.
          </Paragraph>
        </Panel>

        <Panel header="Safety limits (why rewards sometimes pause)" key="safety">
          <Paragraph>
            There is a <Text strong>daily earning cap per viewer</Text> across
            all watch rewards, set in the Affiliate Guru admin (payout rules).
            When a viewer hits it, further rewards fail with “Daily earning
            limit reached” until midnight. Every reward and unlock is also
            recorded exactly once — retries, double taps and app crashes can
            never pay twice.
          </Paragraph>
        </Panel>
      </Collapse>

      <Title level={4}>Quick setup checklist for a new series</Title>
      <Paragraph>
        1. Set <Text strong>Free Episodes</Text> on the series (e.g. 2).
        <br />
        2. On <Text strong>Watch Earn Config</Text>: switch the feature ON and
        set the series’ Episodes (X) and Coins (R).
        <br />
        3. Check <Text strong>Unlock Config</Text> bundle price/size — the
        reward should cover at least one bundle (R ≥ bundle price), otherwise
        viewers earn coins but nothing auto-unlocks.
        <br />
        4. Done — the app picks it up within about 5 minutes.
      </Paragraph>
      <Paragraph type="secondary">
        Rule of thumb: make R a little larger than the bundle price (e.g.
        reward 200 vs bundle 150) so every completed task visibly opens new
        episodes and leaves a small coin balance as a bonus.
      </Paragraph>
    </Typography>
  </div>
);

export default HowItWorks;
